// src/components/common/ExportFormatPicker.tsx
/**
 * Shared export format picker modal.
 * Lets the user pick xlsx / csv / pdf and triggers exportReport().
 * Used by Chart of accounts, Bank transactions, Account List report.
 */

import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { X, FileText, FileSpreadsheet, FileType } from 'lucide-react';
import type { ExportFormat, ExportOptions } from '../../utils/exportReport';
import { exportReport } from '../../utils/exportReport';

interface ExportFormatPickerProps {
  open: boolean;
  onClose: () => void;
  reportName: string;
  options: ExportOptions;
}

interface FormatRow {
  value: ExportFormat;
  label: string;
  description: string;
  Icon: React.ComponentType<{ size?: number; strokeWidth?: number; className?: string }>;
}

const FORMATS: FormatRow[] = [
  { value: 'xlsx', label: 'Excel (.xlsx)', description: 'Formatted spreadsheet, formulas supported', Icon: FileSpreadsheet },
  { value: 'csv', label: 'CSV (.csv)', description: 'Plain comma-separated values', Icon: FileText },
  { value: 'pdf', label: 'PDF (.pdf)', description: 'Formatted document, ready to share', Icon: FileType },
];

export default function ExportFormatPicker({ open, onClose, reportName, options }: ExportFormatPickerProps) {
  const [selected, setSelected] = useState<ExportFormat>('xlsx');

  if (!open) return null;

  const handleDownload = () => {
    exportReport(selected, options);
    onClose();
  };

  return createPortal(
    <div
      onClick={onClose}
      className="fixed inset-0 z-[1000] bg-black/40 flex items-center justify-center p-4"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white border border-line rounded-xl shadow-2xl p-6 w-full max-w-[440px]"
      >
        <div className="flex justify-between items-start mb-1">
          <h4 className="text-[17px] font-extrabold text-ink m-0">Export {reportName}</h4>
          <button
            type="button"
            onClick={onClose}
            className="text-ink3 cursor-pointer border-none bg-transparent p-1 -m-1 hover:text-ink"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>
        <p className="text-[12.5px] text-ink3 m-0 mb-4">
          Choose a format. The file saves to your Downloads folder.
        </p>

        <div className="flex flex-col gap-2 mb-5">
          {FORMATS.map(function (f) {
            const isSel = selected === f.value;
            return (
              <button
                key={f.value}
                type="button"
                onClick={() => setSelected(f.value)}
                className={`flex items-center gap-3 p-3 rounded-lg border text-left cursor-pointer bg-white hover:bg-[#FAFBFB] ${
                  isSel ? 'border-teal-dark bg-[#F4FAF7]' : 'border-line'
                }`}
              >
                <span className="w-9 h-9 inline-flex items-center justify-center text-ink3 flex-shrink-0">
                  <f.Icon size={24} strokeWidth={1.5} />
                </span>
                <span className="flex-1">
                  <span className="block text-[13px] font-bold text-ink">{f.label}</span>
                  <span className="block text-[11.5px] text-ink3 mt-0.5">{f.description}</span>
                </span>
                <span
                  className={`w-4 h-4 rounded-full border-2 flex-shrink-0 relative ${
                    isSel ? 'border-teal-dark' : 'border-line'
                  }`}
                >
                  {isSel && <span className="absolute inset-[2px] rounded-full bg-teal-dark block" />}
                </span>
              </button>
            );
          })}
        </div>

        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="text-ink3 text-[13px] font-bold px-4 py-2 border-none bg-transparent cursor-pointer hover:text-ink"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleDownload}
            className="bg-teal-dark text-white text-[13px] font-bold px-6 py-2 rounded-lg border-none cursor-pointer hover:brightness-110"
          >
            Download
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}